import React, { useMemo, useRef } from 'react'
import { Box, Button, Dialog, DialogProps, Stack, styled, Typography } from '@mui/material'
import { CANCEL_BTN_ATTRIBUTES } from '@app/constants'

export type BasicModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface BasicModalProps {
  open: boolean
  size?: BasicModalSize
  title?: string
  submitText?: string
  cancelText?: string
  noCancel?: boolean
  backdropDisabled?: boolean
  modalProps?: { [key: string]: string }
  submitButtonProps?: { [key: string]: any }
  cancelButtonProps?: { [key: string]: any }
  onClose?: () => void
  onSubmit?: () => void
  onExited?: () => void
  actions?: React.ReactNode
  footer?: React.ReactNode
}

const MODAL_TITLE_ID = 'basic-modal-title'

export const BasicModal: React.FC<React.PropsWithChildren<BasicModalProps>> = props => {
  const {
    open,
    onClose,
    onSubmit,
    onExited,
    size = 'md',
    title,
    noCancel,
    backdropDisabled,
    submitText,
    cancelText,
    modalProps,
    submitButtonProps,
    cancelButtonProps = CANCEL_BTN_ATTRIBUTES,
    actions,
    footer,
    children,
  } = props


  const modalWrapperRef = useRef<HTMLDivElement>(null)

  const transitionProps = useMemo(
    () => ({
      onEntered: () => {
        // a11y: focus the role="document" region so NVDA enters Browse Mode,
        // enabling up/down arrow navigation through dialog content.
        modalWrapperRef.current?.focus()
      },
      onExited: () => {
        onExited?.()
      },
    }),
    [onExited],
  )

  const handleClose = (_: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (backdropDisabled && reason) {
      return
    }

    onClose && onClose()
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      size={size}
      TransitionProps={transitionProps}
      // a11y: disableEnforceFocus stops MUI's FocusTrap from actively pulling
      // focus back, which interferes with ChromeVox's virtual cursor (Search+Arrow).
      disableEnforceFocus
      // a11y: disableAutoFocus prevents MUI from briefly focusing the first button
      // before onEntered fires. Without this, NVDA announces "dialog" twice — once
      // for the button focus and once when we redirect focus to the document region.
      disableAutoFocus
      // a11y: pass null to remove MUI's auto-generated dangling aria-labelledby
      // (e.g. ":rb:") that points to no element. The title is read naturally
      // when NVDA browses the role="document" region, avoiding double-reading.
      PaperProps={{ 'aria-labelledby': null, 'aria-modal': true, tabIndex: -1 }}
    >
      {/* a11y: role="document" lets ChromeVox/NVDA use Browse Mode. tabIndex={-1} lets us
           programmatically focus it in onEntered so NVDA enters Browse Mode immediately. */}
      <ModalWrapper {...modalProps} role='document' ref={modalWrapperRef} tabIndex={-1}>
        {title && (
          <ModalTitle id={MODAL_TITLE_ID} variant='h1' component='h2'>
            {title}
          </ModalTitle>
        )}
        {children}
        {actions ?? (
          <StyledButtonGroup role='presentation'>
            <Button
              color='secondary'
              variant='contained'
              onClick={onSubmit}
              {...submitButtonProps}
            >
              {submitText || 'Submit'}
            </Button>
            {!noCancel && (
              <Button
                color='grey'
                variant='text'
                size='small'
                onClick={onClose}
                {...cancelButtonProps}
              >
                {cancelText || 'Cancel'}
              </Button>
            )}
          </StyledButtonGroup>
        )}
      </ModalWrapper>
      {footer}
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: prop => prop !== 'size',
})<DialogProps & { size?: BasicModalSize }>(({ theme, size = 'md' }) => ({
  zIndex: 2500,
  '.MuiDialog-container': {
    [theme.breakpoints.down('tablet')]: {
      height: 'auto',
    },
  },

  '.MuiPaper-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    maxWidth: '350px',
    width: '100%',

    ...(size === 'sm' && { maxWidth: '500px' }),
    ...(size === 'md' && { maxWidth: '700px' }),
    ...(size === 'lg' && { maxWidth: '950px' }),
    ...(size === 'xl' && { maxWidth: '1300px' }),

    [theme.breakpoints.down('tablet')]: {
      height: '100vh',
      margin: theme.spacing(0),
    },
  },
}))

const ModalWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 12, 8),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  outline: 'none',
}))

const ModalTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
})) as typeof Typography

const StyledButtonGroup = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(8),
  gap: theme.spacing(4),
  alignItems: 'center',
}))
